import { FileTypeEnum, WorkflowStatusEnum } from '@ecdlink/graphql';
import { IonContent } from '@ionic/react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Step } from '../../../components/step-viewer/components/step';
import { StepViewer } from '../../../components/step-viewer/step-viewer';
import { useOnlineStatus } from '@hooks/useOnlineStatus';
import { useStaticData } from '@hooks/useStaticData';
import { ChildRegistrationFormState } from '@models/child/child';
import { ChildBirthCertificateFormModel } from '@schemas/child/child-registration/child-birth-certificate-form';
import { useAppDispatch } from '@store';
import { childrenSelectors } from '@store/children';
import { documentActions, documentThunkActions } from '@store/document';
import { userSelectors } from '@store/user';
import * as childRegisterUtils from '@utils/child/child-registration.utils';
import { ChildBirthCertificateForm } from '../child-registration/child-birth-certificate-form/child-birth-certificate-form';
import {
  ChildRegistrationRouteState,
  ChildRegistrationSteps,
} from '../child-registration/child-registration.types';
import { useThunkFetchCall } from '@/hooks/useThunkFetchCall';
import { DocumentsActions } from '@/store/document/document.actions';

export const ChildRegistrationBirthCertificate: React.FC = () => {
  const history = useHistory();
  const appDispatch = useAppDispatch();
  const { getWorkflowStatusIdByEnum, getDocumentTypeIdByEnum } =
    useStaticData();
  const location = useLocation<ChildRegistrationRouteState>();
  const childId = location.state.childId;
  const { isOnline } = useOnlineStatus();
  const user = useSelector(userSelectors.getUser);
  const existingChild = useSelector(childrenSelectors.getChildById(childId));
  const existingChildUser = useSelector(
    childrenSelectors.getChildUserById(existingChild?.userId)
  );

  const [formState, setFormState] = useState<ChildRegistrationFormState>({});

  const { isLoading } = useThunkFetchCall(
    'documents',
    DocumentsActions.CREATE_DOCUMENT
  );

  const getFileTypeFromBase64 = (base64String: string): string | null => {
    if (!base64String) return null;

    if (base64String.startsWith('data:application/pdf')) {
      return '.pdf';
    }
    if (base64String.startsWith('data:image/')) {
      return '.png';
    }
    return null;
  };

  const saveChildBirthCertificate = async (
    birthCertificateForm: ChildBirthCertificateFormModel
  ) => {
    if (
      !birthCertificateForm?.birthCertificateImage ||
      !existingChildUser?.id
    ) {
      console.error(
        'child-registration-birth-certificate:saveChildBirthCertificate existingChildUser?.id undefined'
      );
      exitRegistration();
      return;
    }

    const fileName =
      `${birthCertificateForm.birthCertificateType}` +
      getFileTypeFromBase64(birthCertificateForm?.birthCertificateImage);

    const documentStatusId = getWorkflowStatusIdByEnum(
      WorkflowStatusEnum.DocumentPendingVerification
    );
    const fileType =
      birthCertificateForm?.birthCertificateType === 'clinicCard'
        ? FileTypeEnum.ChildClinicCard
        : FileTypeEnum.ChildBirthCertificate;

    const typeId = getDocumentTypeIdByEnum(fileType);

    const documentInputModel = childRegisterUtils.mapDocumentDto(
      existingChildUser?.id || '',
      fileName,
      documentStatusId || '',
      typeId || '',
      fileType,
      birthCertificateForm.birthCertificateImage,
      user
    );

    appDispatch(documentActions.createDocument(documentInputModel));

    if (isOnline) {
      await appDispatch(
        documentThunkActions.createDocument({ document: documentInputModel })
      ).unwrap();
    }

    exitRegistration();
  };

  const exitRegistration = () => {
    history.goBack();
  };

  useEffect(() => {
    let updatedFormState = { ...formState };
    if (existingChild && existingChildUser) {
      updatedFormState = {
        ...updatedFormState,
      };

      setFormState(updatedFormState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <IonContent scrollY={true}>
        <StepViewer
          activeStep={ChildRegistrationSteps.childBirthCertificateForm}
          showStepCount={false}
          title="Child registration"
          onBack={() => exitRegistration()}
          isOnline={isOnline}
          showOfflineCard={false}
        >
          <Step
            stepKey={ChildRegistrationSteps.childBirthCertificateForm}
            viewBannerWapper
          >
            <ChildBirthCertificateForm
              isLoading={isLoading}
              isSingleForm={true}
              childBirthCertificateForm={
                formState.childBirthCertificateFormModel
              }
              childInformation={formState.childInformationFormModel}
              onSubmit={(form) => {
                saveChildBirthCertificate(form);
              }}
            />
          </Step>
        </StepViewer>
      </IonContent>
    </>
  );
};
